import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Plus, RefreshCw, Lock, Unlock } from 'lucide-react';
import http from '../api/http';
import BlueHeader from '../components/BlueHeader';
import {
  Avatar, Field, IdChip, ListCard, Modal, RowActions, StatCard, StatusBadge, Switch, Table,
  iconBtn, inputCls, rowCls, tdCls,
} from '../components/ui';

const DATE_FIELDS = ['date_ouverture', 'date_debut', 'date_fin', 'date_cloture'];

// AAAA-MM-JJ[...] -> JJ/MM/AAAA (sans passer par Date, pour éviter les décalages de fuseau)
const fmt = (v) => {
  if (!v) return '-';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
};

const Annees = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await http.get('/api/annees');
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const stats = useMemo(() => {
    const cloturees = data.filter((i) => i.etat).length;
    return { total: data.length, cloturees, ouvertes: data.length - cloturees };
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.filter((i) => String(i.libelle_annee || '').toLowerCase().includes(q)) : data;
  }, [data, search]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({ etat: 0 });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    const next = { ...item };
    // Les champs <input type="date"> n'acceptent que AAAA-MM-JJ
    DATE_FIELDS.forEach((k) => { next[k] = item[k] ? String(item[k]).slice(0, 10) : ''; });
    setEditingItem(item);
    setFormData(next);
    setShowModal(true);
  };

  const handleToggleCloture = async (item) => {
    try {
      await http.put(`/api/annees/${item.idannee}`, { etat: item.etat ? 0 : 1 });
      fetchData();
    } catch (error) {
      alert('Erreur lors de la modification: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette année ?')) return;
    try {
      await http.delete(`/api/annees/${id}`);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) await http.put(`/api/annees/${editingItem.idannee}`, formData);
      else await http.post('/api/annees', formData);
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const set = (key) => (e) => setFormData({ ...formData, [key]: e.target.value });

  return (
    <div className="min-h-full bg-slate-50 dark:bg-slate-950">
      <BlueHeader
        title="Années"
        subtitle="Gestion des années fiscales"
        icon={Calendar}
        showBack={false}
        actions={[
          { label: 'Créer', icon: Plus, onClick: handleCreate, primary: true },
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchData, primary: false },
        ]}
      />

      <div className="space-y-6 p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={Calendar} label="Années" value={stats.total} />
          <StatCard icon={Unlock} label="Ouvertes" value={stats.ouvertes} />
          <StatCard icon={Lock} label="Clôturées" value={stats.cloturees} />
        </div>

        <ListCard
          count={filtered.length} singular="année affichée" plural="années affichées"
          search={search} onSearch={setSearch} placeholder="Rechercher une année"
          loading={loading} hasData={data.length > 0} icon={Calendar}
          emptyTitle="Aucune année pour le moment" emptyText="Créez votre première année fiscale pour commencer."
          onCreate={handleCreate} createLabel="Créer une année" createIcon={Plus}
        >
          <Table heads={['Année', 'Ouverture', 'Début', 'Fin', 'Statut', 'Actions']}>
            {filtered.map((item) => (
              <tr key={item.idannee} className={rowCls}>
                <td className={tdCls}>
                  <div className="flex items-center gap-3">
                    <Avatar icon={Calendar} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.libelle_annee}</p>
                      <p className="mt-0.5"><IdChip>#{item.idannee}</IdChip></p>
                    </div>
                  </div>
                </td>
                <td className={`${tdCls} text-sm text-slate-700 dark:text-slate-300`}>{fmt(item.date_ouverture)}</td>
                <td className={`${tdCls} text-sm text-slate-700 dark:text-slate-300`}>{fmt(item.date_debut)}</td>
                <td className={`${tdCls} text-sm text-slate-700 dark:text-slate-300`}>{fmt(item.date_fin)}</td>
                <td className={tdCls}><StatusBadge active={!item.etat} on="Ouverte" off="Clôturée" /></td>
                <td className={tdCls}>
                  <RowActions onEdit={() => handleEdit(item)} onDelete={() => handleDelete(item.idannee)}>
                    <button
                      type="button"
                      onClick={() => handleToggleCloture(item)}
                      className={iconBtn}
                      title={item.etat ? "Ouvrir l'année" : "Clôturer l'année"}
                    >
                      {item.etat ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                    </button>
                  </RowActions>
                </td>
              </tr>
            ))}
          </Table>
        </ListCard>
      </div>

      {showModal && (
        <Modal
          title={editingItem ? "Modifier l'année" : 'Créer une année'}
          subtitle={editingItem ? editingItem.libelle_annee : "Renseignez les dates de l'année fiscale"}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          confirmLabel={editingItem ? 'Enregistrer' : 'Créer'}
        >
          <div className="grid grid-cols-1 gap-5 p-6 sm:grid-cols-2">
            <Field label="Libellé de l'année" span>
              <input type="text" className={inputCls} value={formData.libelle_annee || ''} onChange={set('libelle_annee')} required placeholder="Ex: 2024" />
            </Field>
            <Field label="Date d'ouverture">
              <input type="date" className={inputCls} value={formData.date_ouverture || ''} onChange={set('date_ouverture')} required />
            </Field>
            <Field label="Date de début">
              <input type="date" className={inputCls} value={formData.date_debut || ''} onChange={set('date_debut')} required />
            </Field>
            <Field label="Date de fin">
              <input type="date" className={inputCls} value={formData.date_fin || ''} onChange={set('date_fin')} required />
            </Field>
            <Field label="Date de clôture">
              <input type="date" className={inputCls} value={formData.date_cloture || ''} onChange={set('date_cloture')} />
            </Field>
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/40 sm:col-span-2">
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white">Clôturer l'année</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {formData.etat ? 'Année clôturée' : 'Année ouverte'}
                </p>
              </div>
              <Switch checked={!!formData.etat} label="Clôturer l'année" onChange={(v) => setFormData({ ...formData, etat: v ? 1 : 0 })} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Annees;